import { useEffect, useRef } from 'react';
import type { WsMessage } from '../types';

/**
 * How often the open socket pings the BFF. Each ping is forwarded as one agent-state heartbeat by
 * the task holding the socket, and agent-state reaps an agent silent for 15s — so this is the
 * agent's presence. No tab, no pings, no presence: closing the desktop logs the agent out of
 * routing within 15s, which is what should happen.
 */
export const PING_INTERVAL_MS = 5000;

/**
 * One WebSocket to the BFF; reconnects with backoff while a token is present; pings while open.
 * A refreshed token does not reopen the socket: each (re)connect reads the latest one.
 */
export function useWs(token: string | null, onMessage: (m: WsMessage) => void, onOpen?: () => void, onClose?: () => void) {
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const loggedIn = token !== null;
  const handler = useRef(onMessage);
  handler.current = onMessage;
  const opener = useRef(onOpen);
  opener.current = onOpen;
  const closer = useRef(onClose);
  closer.current = onClose;
  useEffect(() => {
    if (!loggedIn) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let delay = 1000;
    let ping: ReturnType<typeof setInterval> | null = null;
    const stopPing = () => { if (ping) { clearInterval(ping); ping = null; } };
    const connect = () => {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const current = tokenRef.current;
      if (!current) return;
      // Token as a subprotocol, not a query param, so it never lands in a URL or access log (AE-10888).
      const sock = new WebSocket(`${proto}://${location.host}/ws`, ['bearer', current]);
      ws = sock;
      sock.onopen = () => {
        delay = 1000;
        stopPing();
        ping = setInterval(() => { if (sock.readyState === WebSocket.OPEN) sock.send(JSON.stringify({ type: 'ping' })); }, PING_INTERVAL_MS);
        opener.current?.();
      };
      sock.onmessage = (ev) => { try { handler.current(JSON.parse(ev.data as string) as WsMessage); } catch { /* ignore */ } };
      sock.onclose = () => {
        stopPing();
        if (closed) return;
        closer.current?.();
        // Full jitter (AE-10930): a BFF deploy closes every desktop at once; a thousand tabs must
        // not all reconnect on the same tick.
        setTimeout(connect, 250 + Math.random() * delay);
        delay = Math.min(delay * 2, 10000);
      };
      sock.onerror = () => sock.close();
    };
    connect();
    return () => { closed = true; stopPing(); ws?.close(); };
  }, [loggedIn]);
}
